import { log } from '@/utils/logger'
import { configService } from '@/config'
import { PublicKey } from '@solana/web3.js'
import { getX1TokenIdentity } from '@/utils/knownX1Tokens'

export interface Token2022Extensions {
  // Transfer fee extension
  transferFee?: {
    transferFeeBasisPoints: number  // Fee in basis points (100 = 1%)
    maximumFee: string
    withheldAmount?: string
  }
  // Interest-bearing extension
  interestBearing?: {
    currentRate: number  // Rate in basis points
    preUpdateAverageRate?: number
  }
  // Non-transferable (soul-bound)
  nonTransferable?: boolean
  // Permanent delegate
  permanentDelegate?: string
  // Transfer hook
  transferHook?: {
    authority?: string
    programId: string
  }
  // Confidential transfers
  confidentialTransfer?: boolean
  // Default account state (frozen by default)
  defaultAccountState?: 'initialized' | 'frozen'
  // Metadata pointer (external metadata)
  metadataPointer?: string
  // Close authority
  closeAuthority?: string
  // Group/member pointers
  groupPointer?: string
  memberPointer?: string
}

export interface TokenMetadata {
  name?: string
  symbol?: string
  image?: string
  description?: string
  uri?: string
  attributes?: Array<{ trait_type: string; value: string | number }>
  // Token-2022 extensions
  extensions?: Token2022Extensions
  isToken2022?: boolean
}

export interface ExtendedTokenMetadata extends TokenMetadata {
  decimals: number
  supply: string
  holders?: number
  programId?: string
}

/**
 * Service for fetching comprehensive token metadata from multiple sources:
 * 1. Metaplex Token Metadata Program (PDA approach)
 * 2. Token-2022 metadata extensions
 * 3. Solana token list (community curated)
 * 4. Off-chain JSON via URI
 */
export class TokenMetadataService {
  private static instance: TokenMetadataService
  private cache = new Map<string, { metadata: TokenMetadata; timestamp: number }>()
  private readonly CACHE_DURATION = 5 * 60 * 1000 // 5 minutes
  private readonly METAPLEX_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s')

  private constructor() {}

  public static getInstance(): TokenMetadataService {
    if (!TokenMetadataService.instance) {
      TokenMetadataService.instance = new TokenMetadataService()
    }
    return TokenMetadataService.instance
  }

  /**
   * Main method to get token metadata from all available sources
   */
  async getTokenMetadata(mintAddress: string): Promise<TokenMetadata> {
    // X1 native-token identity takes precedence over ANY resolved source. The
    // wrapped-native mint (So111…112) is wrapped XNT on this Solana fork, NOT
    // "Wrapped SOL" as the community token list claims. Short-circuit known X1
    // mints to the canonical symbol/name/logo (no network needed).
    const x1Identity = getX1TokenIdentity(mintAddress)
    if (x1Identity) {
      return {
        symbol: x1Identity.symbol,
        name: x1Identity.name,
        image: x1Identity.logo,
      }
    }

    // Check cache first
    const cached = this.cache.get(mintAddress)
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.metadata
    }

    try {
      log.debug(`🔍 Fetching metadata for token: ${mintAddress.slice(0, 8)}...`)

      // Method 0: Our own backend — the canonical, registry-overlaid source
      // (ClickHouse xdex_tokens_snapshot + canonicalTokens choke points). This
      // is what the /token/:mint page renders, so the tx page and every other
      // consumer agree with it. Also the only lane that never depends on
      // third-party CORS policies (Metaplex off-chain JSONs frequently live on
      // hosts like catbox.moe that block browser fetches).
      const backendMetadata = await this.getBackendMetadata(mintAddress)
      if (backendMetadata.name || backendMetadata.symbol) {
        log.info(`✅ Found backend metadata for ${mintAddress.slice(0, 8)}: ${backendMetadata.name}`)
        this.cache.set(mintAddress, { metadata: backendMetadata, timestamp: Date.now() })
        return backendMetadata
      }

      // Method 1: Try Metaplex metadata (most common)
      const metaplexMetadata = await this.getMetaplexMetadata(mintAddress)
      if (metaplexMetadata.name || metaplexMetadata.symbol) {
        log.info(`✅ Found Metaplex metadata for ${mintAddress.slice(0, 8)}: ${metaplexMetadata.name}`)

        // If we have a URI, fetch off-chain metadata
        if (metaplexMetadata.uri) {
          const offChainMetadata = await this.fetchOffChainMetadata(metaplexMetadata.uri)
          const combinedMetadata = { ...metaplexMetadata, ...offChainMetadata }
          this.cache.set(mintAddress, { metadata: combinedMetadata, timestamp: Date.now() })
          return combinedMetadata
        }

        this.cache.set(mintAddress, { metadata: metaplexMetadata, timestamp: Date.now() })
        return metaplexMetadata
      }

      // Method 2: Try Token-2022 metadata extensions
      const token2022Metadata = await this.getToken2022Metadata(mintAddress)
      if (token2022Metadata.name || token2022Metadata.symbol) {
        log.info(`✅ Found Token-2022 metadata for ${mintAddress.slice(0, 8)}: ${token2022Metadata.name}`)
        this.cache.set(mintAddress, { metadata: token2022Metadata, timestamp: Date.now() })
        return token2022Metadata
      }

      // Method 3: Check Solana token list (fallback)
      const tokenListMetadata = await this.getTokenListMetadata(mintAddress)
      if (tokenListMetadata.name || tokenListMetadata.symbol) {
        log.info(`✅ Found token list metadata for ${mintAddress.slice(0, 8)}: ${tokenListMetadata.name}`)
        this.cache.set(mintAddress, { metadata: tokenListMetadata, timestamp: Date.now() })
        return tokenListMetadata
      }

      // No metadata found
      const emptyMetadata = {}
      this.cache.set(mintAddress, { metadata: emptyMetadata, timestamp: Date.now() })
      return emptyMetadata

    } catch (error) {
      log.warn(`⚠️ Failed to get metadata for ${mintAddress}:`, error)
      return {}
    }
  }

  /**
   * Get metadata from the explorer backend (/api/tokens/:mint/info) — the
   * canonical source: ClickHouse token snapshot with the canonical-token
   * registry overrides already applied server-side.
   */
  private async getBackendMetadata(mintAddress: string): Promise<TokenMetadata> {
    try {
      const network = configService.getCurrentNetworkConfig().name
      const response = await fetch(
        `/api/tokens/${mintAddress}/info?network=${encodeURIComponent(network)}`,
        { headers: { Accept: 'application/json' } },
      )
      if (!response.ok) return {}
      const info = await response.json()
      return {
        name: info?.name || undefined,
        symbol: info?.symbol || undefined,
        image: info?.image || undefined,
        description: info?.description || undefined,
      }
    } catch (error) {
      log.debug(`Could not fetch backend metadata for ${mintAddress}:`, error)
      return {}
    }
  }

  /**
   * Get metadata from Metaplex Token Metadata Program
   */
  private async getMetaplexMetadata(mintAddress: string): Promise<TokenMetadata> {
    try {
      // Derive metadata PDA
      const metadataPda = await this.deriveMetadataPda(mintAddress)

      // Get the metadata account
      const rpcUrl = configService.getCurrentNetworkConfig().rpcUrl
      const response = await fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'getAccountInfo',
          params: [metadataPda, { encoding: 'base64' }]
        })
      })

      const result = await response.json()
      if (!result.result?.value?.data) {
        return {}
      }

      // Decode the metadata account data
      const accountData = result.result.value.data[0] // base64 data
      const metadata = this.decodeMetaplexMetadata(accountData)

      return metadata
    } catch (error) {
      log.debug(`Could not fetch Metaplex metadata for ${mintAddress}:`, error)
      return {}
    }
  }

  /**
   * Derive Metaplex metadata PDA
   */
  private async deriveMetadataPda(mintAddress: string): Promise<string> {
    try {
      const mintPubkey = new PublicKey(mintAddress)
      // Uint8Array seeds only — the browser bundle has no global Buffer, and a
      // bare Buffer.from() here threw for EVERY mint, silently killing the
      // whole Metaplex lane (tokens rendered as short mints).
      const [metadataPda] = PublicKey.findProgramAddressSync(
        [
          new TextEncoder().encode('metadata'),
          this.METAPLEX_PROGRAM_ID.toBytes(),
          mintPubkey.toBytes(),
        ],
        this.METAPLEX_PROGRAM_ID
      )
      return metadataPda.toString()
    } catch (error) {
      log.debug(`Failed to derive metadata PDA for ${mintAddress}:`, error)
      throw error
    }
  }

  /**
   * Decode Metaplex metadata from base64 account data
   */
  private decodeMetaplexMetadata(base64Data: string): TokenMetadata {
    try {
      // Browser-safe decode (no Node Buffer): atob → Uint8Array + DataView.
      const raw = atob(base64Data)
      const bytes = new Uint8Array(raw.length)
      for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
      const view = new DataView(bytes.buffer)
      const textDecoder = new TextDecoder('utf-8')
      const buffer = {
        length: bytes.length,
        readUInt8: (o: number) => view.getUint8(o),
        readUInt32LE: (o: number) => view.getUint32(o, true),
        utf8Slice: (start: number, end: number) =>
          textDecoder.decode(bytes.subarray(start, end)),
      }

      // Manual parsing of Metaplex metadata structure
      // Metaplex metadata account structure:
      // - 1 byte: account type (should be 4 for metadata)
      // - 32 bytes: update authority
      // - 32 bytes: mint
      // - 4 bytes: name string length + name string
      // - 4 bytes: symbol string length + symbol string
      // - 4 bytes: uri string length + uri string

      if (buffer.length < 1) return {}

      const accountType = buffer.readUInt8(0)
      if (accountType !== 4) return {} // Not a metadata account

      let offset = 1 + 32 + 32 // Skip account type + update authority + mint

      // Read name
      if (offset + 4 > buffer.length) return {}
      const nameLength = buffer.readUInt32LE(offset)
      offset += 4

      let name = ''
      if (nameLength > 0 && offset + nameLength <= buffer.length) {
        name = buffer.utf8Slice(offset, offset + nameLength).replace(/\0+$/g, '').trim()
        offset += nameLength
      }

      // Read symbol
      if (offset + 4 > buffer.length) return { name: name || undefined }
      const symbolLength = buffer.readUInt32LE(offset)
      offset += 4

      let symbol = ''
      if (symbolLength > 0 && offset + symbolLength <= buffer.length) {
        symbol = buffer.utf8Slice(offset, offset + symbolLength).replace(/\0+$/g, '').trim()
        offset += symbolLength
      }

      // Read URI
      if (offset + 4 > buffer.length) return { name: name || undefined, symbol: symbol || undefined }
      const uriLength = buffer.readUInt32LE(offset)
      offset += 4

      let uri = ''
      if (uriLength > 0 && offset + uriLength <= buffer.length) {
        uri = buffer.utf8Slice(offset, offset + uriLength).replace(/\0+$/g, '').trim()
      }

      return {
        name: name || undefined,
        symbol: symbol || undefined,
        uri: uri || undefined
      }
    } catch (error) {
      log.debug('Failed to decode Metaplex metadata:', error)
      return {}
    }
  }

  /**
   * Get Token-2022 metadata and all extensions
   */
  private async getToken2022Metadata(mintAddress: string): Promise<TokenMetadata> {
    try {
      const rpcUrl = configService.getCurrentNetworkConfig().rpcUrl
      const response = await fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          id: 1,
          method: 'getAccountInfo',
          params: [mintAddress, { encoding: 'jsonParsed' }]
        })
      })

      const result = await response.json()
      const parsedInfo = result.result?.value?.data?.parsed?.info
      const extensions = parsedInfo?.extensions

      if (!extensions || extensions.length === 0) {
        return {}
      }

      // This is a Token-2022 token with extensions
      const metadata: TokenMetadata = {
        isToken2022: true,
        extensions: {}
      }

      for (const ext of extensions) {
        switch (ext.extension) {
          case 'tokenMetadata':
            // Basic metadata
            metadata.name = ext.state?.name
            metadata.symbol = ext.state?.symbol
            metadata.uri = ext.state?.uri
            break

          case 'transferFeeConfig':
            // Transfer fee extension
            const newerFee = ext.state?.newerTransferFee || ext.state?.olderTransferFee
            if (newerFee) {
              metadata.extensions!.transferFee = {
                transferFeeBasisPoints: newerFee.transferFeeBasisPoints || 0,
                maximumFee: newerFee.maximumFee || '0',
                withheldAmount: ext.state?.withheldAmount
              }
            }
            break

          case 'interestBearingConfig':
            // Interest-bearing extension
            metadata.extensions!.interestBearing = {
              currentRate: ext.state?.currentRate || 0,
              preUpdateAverageRate: ext.state?.preUpdateAverageRate
            }
            break

          case 'nonTransferable':
            // Soul-bound token
            metadata.extensions!.nonTransferable = true
            break

          case 'permanentDelegate':
            // Permanent delegate authority
            metadata.extensions!.permanentDelegate = ext.state?.delegate
            break

          case 'transferHook':
            // Transfer hook program
            if (ext.state?.programId) {
              metadata.extensions!.transferHook = {
                authority: ext.state?.authority,
                programId: ext.state.programId
              }
            }
            break

          case 'confidentialTransferMint':
            // Confidential transfers enabled
            metadata.extensions!.confidentialTransfer = true
            break

          case 'defaultAccountState':
            // Default state for new accounts
            metadata.extensions!.defaultAccountState = ext.state?.state
            break

          case 'metadataPointer':
            // External metadata pointer
            metadata.extensions!.metadataPointer = ext.state?.metadataAddress
            break

          case 'mintCloseAuthority':
            // Close authority
            metadata.extensions!.closeAuthority = ext.state?.closeAuthority
            break

          case 'groupPointer':
            // Token group
            metadata.extensions!.groupPointer = ext.state?.groupAddress
            break

          case 'groupMemberPointer':
            // Token group member
            metadata.extensions!.memberPointer = ext.state?.memberAddress
            break
        }
      }

      // Only return if we found useful data
      if (metadata.name || metadata.symbol || Object.keys(metadata.extensions || {}).length > 0) {
        return metadata
      }

      return {}
    } catch (error) {
      log.debug(`Could not fetch Token-2022 metadata for ${mintAddress}:`, error)
      return {}
    }
  }

  /**
   * Get metadata from Solana token list (community curated)
   */
  private async getTokenListMetadata(mintAddress: string): Promise<TokenMetadata> {
    try {
      // Fetch the official Solana token list
      const response = await fetch('https://raw.githubusercontent.com/solana-labs/token-list/main/src/tokens/solana.tokenlist.json')
      const tokenList = await response.json()

      const token = tokenList.tokens.find((t: any) => t.address === mintAddress)
      if (token) {
        return {
          name: token.name,
          symbol: token.symbol,
          image: token.logoURI
        }
      }

      return {}
    } catch (error) {
      log.debug(`Could not fetch token list metadata for ${mintAddress}:`, error)
      return {}
    }
  }

  /**
   * Fetch off-chain metadata from URI (Arweave, IPFS, etc.)
   */
  private async fetchOffChainMetadata(uri: string): Promise<TokenMetadata> {
    try {
      // Handle IPFS and Arweave URIs
      let fetchUrl = uri
      if (uri.startsWith('ipfs://')) {
        fetchUrl = uri.replace('ipfs://', 'https://ipfs.io/ipfs/')
      }

      const response = await fetch(fetchUrl, {
        headers: {
          'Accept': 'application/json'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      const metadata = await response.json()

      return {
        name: metadata.name,
        symbol: metadata.symbol,
        image: metadata.image,
        description: metadata.description,
        attributes: metadata.attributes
      }
    } catch (error) {
      log.debug(`Could not fetch off-chain metadata from ${uri}:`, error)
      return {}
    }
  }

  /**
   * Clear the metadata cache
   */
  clearCache(): void {
    this.cache.clear()
  }
}

export const tokenMetadataService = TokenMetadataService.getInstance()